import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminBreadcrumbs } from "@/components/admin/AdminBreadcrumbs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
const EMAIL_KEYS = [
  { key: "confirmation", label: "Confirme seu e-mail", vars: "{{nome}}, {{confirmationUrl}}" },
  { key: "account_created", label: "Conta criada", vars: "{{nome}}" },
  { key: "welcome", label: "Boas-vindas", vars: "{{nome}}" },
  { key: "email_reminder", label: "Lembrete de uso (24h após cadastro)", vars: "{{nome}}" },
  { key: "suggest_subscription", label: "Sugestão de assinatura", vars: "{{nome}}" },
  { key: "password_reset", label: "Redefinição de senha", vars: "{{nome}}, {{resetUrl}}" },
  { key: "password_changed", label: "Senha alterada", vars: "{{nome}}, {{data}}, {{hora}}" },
  { key: "subscription_confirmed", label: "Assinatura ativada", vars: "{{nome}}, {{plano}}, {{valor}}, {{forma_pagamento}}, {{proxima_cobranca}}, {{data_pagamento}}" },
  { key: "plan_changed", label: "Plano atualizado", vars: "{{nome}}, {{plano}}, {{valor}}, {{proxima_cobranca}}" },
  { key: "subscription_cancelled", label: "Assinatura cancelada", vars: "{{nome}}, {{data_final}}" },
  { key: "payment_failed", label: "Pagamento não processado", vars: "{{nome}}, {{plano}}, {{valor}}, {{data_tentativa}}" },
  { key: "subscription_suspended", label: "Assinatura suspensa", vars: "{{nome}}, {{plano}}, {{valor}}, {{data_tentativa}}" },
  { key: "payment_confirmed", label: "Pagamento confirmado", vars: "{{nome}}, {{plano}}, {{valor}}, {{forma_pagamento}}, {{proxima_cobranca}}, {{data_pagamento}}" },
  { key: "patient_limit_reached", label: "Limite de pacientes atingido", vars: "{{nome}}, {{plano}}, {{limite_pacientes}}" },
] as const;

interface EmailTemplate {
  subject: string;
  title: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
  secondaryButtonText?: string;
  secondaryButtonUrl?: string;
  /** Se false, o email não é disparado. Default true. */
  enabled?: boolean;
}

const defaultTemplate: EmailTemplate = {
  subject: "",
  title: "",
  description: "",
  buttonText: "",
  buttonUrl: "",
  enabled: true,
};

export default function ConfiguracoesEmails() {
  const [templates, setTemplates] = useState<Record<string, EmailTemplate>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("config")
        .select("key, value")
        .eq("scope", "email")
        .in("key", EMAIL_KEYS.map((e) => e.key));

      if (error) throw error;

      const map: Record<string, EmailTemplate> = {};
      for (const row of data ?? []) {
        const v = row.value as unknown as EmailTemplate;
        map[row.key] = { ...defaultTemplate, ...v, enabled: v?.enabled ?? true };
      }
      for (const { key } of EMAIL_KEYS) {
        if (!map[key]) map[key] = { ...defaultTemplate };
      }
      setTemplates(map);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = (key: string, field: "subject" | "description" | "enabled", value: string | boolean) => {
    setTemplates((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? defaultTemplate), [field]: value },
    }));
  };

  const saveTemplate = async (key: string, override?: Partial<EmailTemplate>) => {
    try {
      setSaving(key);
      const t = override ? { ...(templates[key] ?? defaultTemplate), ...override } : templates[key];
      if (!t) return;

      const row = { scope: "email" as const, key, value: t as unknown as Record<string, unknown> };
      const { error } = await supabase
        .from("config")
        .upsert(row as any, { onConflict: "scope,key" });

      if (error) throw error;
      toast.success(`Template "${key}" salvo`);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full size-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <AdminBreadcrumbs />
        <h1 className="text-3xl font-bold tracking-tight mt-2">Templates de E-mail</h1>
        <p className="text-muted-foreground">
          Edite apenas o assunto (inbox) e a descrição. Título e botões são fixos por template.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>14 E-mails Transacionais</CardTitle>
          <CardDescription>
            Variáveis disponíveis por template (somente subject e description são editáveis). O lembrete de uso é enviado 24h após cadastro.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full" defaultValue={["confirmation"]}>
            {EMAIL_KEYS.map(({ key, label, vars }) => {
              const t = templates[key] ?? defaultTemplate;
              return (
                <AccordionItem key={key} value={key}>
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex-1 text-left">{label}</span>
                    <span
                      className="flex items-center gap-2 shrink-0 mr-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        checked={(templates[key] ?? defaultTemplate).enabled !== false}
                        onCheckedChange={(checked) => {
                          const enabled = !!checked;
                          updateTemplate(key, "enabled", enabled);
                          saveTemplate(key, { enabled });
                        }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {(templates[key] ?? defaultTemplate).enabled !== false ? "Ativo" : "Desativado"}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-4 pt-2">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor={`${key}-subject`}>Assunto (inbox)</Label>
                        <Input
                          id={`${key}-subject`}
                          value={t.subject}
                          onChange={(e) => updateTemplate(key, "subject", e.target.value)}
                          placeholder="Assunto do e-mail"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label htmlFor={`${key}-description`}>Descrição</Label>
                        <Textarea
                          id={`${key}-description`}
                          value={t.description}
                          onChange={(e) => updateTemplate(key, "description", e.target.value)}
                          placeholder="Corpo do e-mail (use variáveis)"
                          rows={5}
                        />
                      </div>
                      <div className="rounded-lg border bg-muted/50 p-3 text-sm text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">Campos fixos (somente leitura)</p>
                        <p><strong>Título:</strong> {t.title || "—"}</p>
                        <p><strong>Botão:</strong> {t.buttonText || "—"} → {t.buttonUrl || "—"}</p>
                        <p className="mt-2"><strong>Variáveis:</strong> {vars}</p>
                      </div>
                      <Button
                        onClick={() => saveTemplate(key)}
                        disabled={saving === key}
                      >
                        {saving === key ? "Salvando..." : "Salvar"}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
